import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NextStepAction } from './NextStep';

/**
 * Kritik bir adimdan sonra (ör. B2B oturum basvurusu) kullanicinin gozden
 * kaciramayacagi "simdi ne yapmaliyim?" penceresi. Stil DeleteConfirmModal ile ayni ailedendir.
 */

export type NextStepModalTone = 'success' | 'pending';

type NextStepModalProps = {
    isOpen: boolean;
    tone: NextStepModalTone;
    title: string;
    message: string;
    action: NextStepAction;
    dismissLabel: string;
    onClose: () => void;
};

const TONE_ICON: Record<NextStepModalTone, string> = {
    success: 'fa-solid fa-circle-check',
    pending: 'fa-solid fa-hourglass-half',
};

const NextStepModal = ({ isOpen, tone, title, message, action, dismissLabel, onClose }: NextStepModalProps) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKey);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleAction = () => {
        onClose();
        navigate(action.to);
    };

    return (
        <>
            <style>{`
                .nsm-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 99999;
                    background: rgba(23, 37, 84, 0.55);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                    animation: nsm-fade-in 0.15s ease;
                }
                @keyframes nsm-fade-in {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                .nsm-card {
                    background: var(--beetinq-card-bg, #fff);
                    border: 1px solid var(--beetinq-border, #E4EAF2);
                    border-radius: 20px;
                    padding: 32px 28px 24px;
                    width: 100%;
                    max-width: 440px;
                    position: relative;
                    text-align: center;
                    box-shadow: 0 10px 40px rgba(23, 37, 84, 0.18);
                    animation: nsm-slide-up 0.2s ease;
                }
                @keyframes nsm-slide-up {
                    from { opacity: 0; transform: translateY(16px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .nsm-close-btn {
                    position: absolute;
                    top: 14px;
                    right: 14px;
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    border: none;
                    background: var(--beetinq-page-bg, #F3F7FC);
                    color: var(--beetinq-text-secondary, #65718A);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background 0.15s, color 0.15s;
                }
                .nsm-close-btn:hover {
                    background: var(--beetinq-border, #E4EAF2);
                    color: var(--beetinq-text, #172554);
                }
                .nsm-icon {
                    width: 64px;
                    height: 64px;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 30px;
                    margin-bottom: 16px;
                }
                .nsm-icon--success {
                    background: var(--beetinq-success-bg, #DDF7E8);
                    color: var(--beetinq-success, #16A261);
                }
                .nsm-icon--pending {
                    background: var(--beetinq-warning-bg, #FFF1CE);
                    color: var(--beetinq-warning, #D89400);
                }
                .nsm-title {
                    font-size: 20px;
                    font-weight: 700;
                    color: var(--beetinq-text, #172554);
                    margin: 0 0 8px;
                    line-height: 1.3;
                }
                .nsm-message {
                    font-size: 15px;
                    color: var(--beetinq-text-secondary, #65718A);
                    margin: 0 0 24px;
                    line-height: 1.6;
                }
                .nsm-btn-primary {
                    width: 100%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 12px 18px;
                    border: none;
                    border-radius: 999px;
                    background: var(--beetinq-primary, #2563eb);
                    color: #fff;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: background 0.15s, transform 0.1s;
                }
                .nsm-btn-primary:hover {
                    background: var(--beetinq-navy, #1e3a8a);
                    transform: translateY(-1px);
                }
                .nsm-btn-dismiss {
                    margin-top: 10px;
                    width: 100%;
                    padding: 10px 18px;
                    border: none;
                    border-radius: 999px;
                    background: transparent;
                    color: var(--beetinq-text-secondary, #65718A);
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .nsm-btn-dismiss:hover {
                    background: var(--beetinq-page-bg, #F3F7FC);
                    color: var(--beetinq-text, #172554);
                }
                [data-theme-version="dark"] .nsm-card {
                    background: #161e32;
                    border-color: #2a3350;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.45);
                }
                [data-theme-version="dark"] .nsm-title { color: #fff; }
                [data-theme-version="dark"] .nsm-message { color: #aab4c8; }
                [data-theme-version="dark"] .nsm-close-btn,
                [data-theme-version="dark"] .nsm-btn-dismiss:hover {
                    background: #1e3a8a;
                    color: #fff;
                }
                [data-theme-version="dark"] .nsm-btn-dismiss { color: #aab4c8; }
                [data-theme-version="dark"] .nsm-btn-primary:hover { background: #3B78E7; }
            `}</style>

            <div className="nsm-overlay" onClick={onClose}>
                <div
                    className="nsm-card"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="nsm-title"
                    onClick={(event) => event.stopPropagation()}
                >
                    <button type="button" className="nsm-close-btn" onClick={onClose} aria-label={dismissLabel}>
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>

                    <span className={`nsm-icon nsm-icon--${tone}`}>
                        <i className={TONE_ICON[tone]} aria-hidden="true" />
                    </span>

                    <p id="nsm-title" className="nsm-title">{title}</p>
                    <p className="nsm-message">{message}</p>

                    <button type="button" className="nsm-btn-primary" onClick={handleAction} autoFocus>
                        {action.label}
                        <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                    </button>
                    <button type="button" className="nsm-btn-dismiss" onClick={onClose}>
                        {dismissLabel}
                    </button>
                </div>
            </div>
        </>
    );
};

export default NextStepModal;

/**
 * Uygulama genelinde tek pencere: herhangi bir sayfadan `showNextStepModal(...)`
 * ile acilir. Host, App icinde (Router'in altinda) bir kez yerlestirilir.
 */
export type NextStepModalRequest = Omit<NextStepModalProps, 'isOpen' | 'onClose'>;

let openNextStepModal: ((request: NextStepModalRequest) => void) | null = null;

export const showNextStepModal = (request: NextStepModalRequest) => {
    openNextStepModal?.(request);
};

export const NextStepModalHost = () => {
    const [request, setRequest] = useState<NextStepModalRequest | null>(null);
    const close = useCallback(() => setRequest(null), []);

    useEffect(() => {
        openNextStepModal = setRequest;
        return () => {
            openNextStepModal = null;
        };
    }, []);

    if (!request) return null;
    return <NextStepModal isOpen {...request} onClose={close} />;
};
